import React from 'react';
import { motion } from 'framer-motion';
import { BackgroundWrapper } from './components/shared/BackgroundWrapper';
import { TimeDisplay } from './components/shared/TimeDisplay';
import { generateSenderUrl } from './utils/roleDetection';
import { EditableRoomDisplayName } from './components/EditableRoomDisplayName';
import { resolvedRoomDisplayName } from './roomDisplayName';
import { LicenseInfo } from './lib/LicenseInfo';

interface ReceiverLobbyProps {
    roomId: string;
    roomDisplayName?: string;
    onRoomDisplayNameSave?: (name: string) => void;
    onSenderJoined: () => void;
    config: any;
}

export const ReceiverLobby: React.FC<ReceiverLobbyProps> = ({
    roomId: initialRoomId,
    roomDisplayName,
    onRoomDisplayNameSave,
}) => {
    const roomId = initialRoomId;
    const senderUrl = generateSenderUrl();
    const title = resolvedRoomDisplayName(roomDisplayName);
    const canEdit = Boolean(onRoomDisplayNameSave);

    return (
        <BackgroundWrapper>
            {/* Top Left Logo */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="fixed top-6 left-6 z-30"
            >
                <img src="./EllingtonLogo.svg" className="w-20 sm:w-24" alt="logo" />
            </motion.div>

            {/* Top Right Time */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="fixed top-6 right-6 z-30"
            >
                <TimeDisplay />
            </motion.div>

            <div className="flex items-center justify-center min-h-screen px-4 py-12 sm:px-6">
                {/* Center — glass pairing card (frosted panel over background) */}
                <motion.div
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-xl"
                >
                    <div
                        className={[
                            'relative isolate overflow-hidden rounded-[22px]',
                            'border border-white/15',
                            'bg-black/45 backdrop-blur-[18px] backdrop-saturate-150',
                            'shadow-[0_28px_56px_-16px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.05)_inset]',
                        ].join(' ')}
                    >
                        {/* Dark frosted depth */}
                        <div
                            className="pointer-events-none absolute inset-0 rounded-[22px] bg-gradient-to-br from-black/25 via-transparent to-black/40"
                            aria-hidden
                        />
                        <div className="relative space-y-10 px-5 py-10 sm:px-6 sm:py-12 md:px-8">
                            {/* Top label — small caps, wide tracking */}
                            <div className="text-center">
                                <h3 className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm font-medium uppercase tracking-[0.35em] text-white">
                                    <EditableRoomDisplayName
                                        value={title}
                                        onSave={(name) => onRoomDisplayNameSave?.(name)}
                                        editable={canEdit}
                                        className="inline text-sm font-medium uppercase tracking-[0.35em] text-white"
                                        inputClassName="max-w-md text-center bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-base font-normal normal-case tracking-normal text-white"
                                        as="span"
                                    />
                                    <span className="text-white/70">-</span>
                                    <span className="font-normal tracking-[0.35em]">CODE</span>
                                </h3>
                            </div>

                            {/* Pairing code — spaced characters */}
                            <div
                                className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-2 sm:gap-x-4"
                                aria-label={`Pairing code ${roomId}`}
                            >
                                {roomId.split('').map((ch, i) => (
                                    <span
                                        key={`${i}-${ch}`}
                                        className="text-5xl font-bold leading-none text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.35)] sm:text-6xl md:text-7xl"
                                    >
                                        {ch}
                                    </span>
                                ))}
                            </div>

                            {/* Divider — multi-color gradient */}
                            <div
                                className="h-[2px] w-full rounded-full bg-[linear-gradient(90deg,transparent_0%,#fb923c_12%,#fbbf24_28%,#34d399_50%,#38bdf8_72%,#a78bfa_88%,transparent_100%)] opacity-95 shadow-[0_0_20px_rgba(56,189,248,0.25)]"
                                role="presentation"
                            />

                            {/* Visit URL */}
                            <div className="text-center">
                                <p className="text-sm font-normal leading-relaxed text-white/95 sm:text-base">
                                    Visit : <span className="font-medium text-white">{senderUrl}</span> on your
                                    device
                                </p>
                                <LicenseInfo className="mt-3 block text-xs tracking-wide text-white/50" />
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </BackgroundWrapper>
    );
};
